// React
import { RefObject, useMemo } from "react";
// MUI
import { GridColDef, GridGroupingColDefOverride } from "@mui/x-data-grid-pro";
import { GridApi, GridRenderCellParams } from "@mui/x-data-grid-premium";

// Helpers
import { getDateInUTC, getFormattedDate } from "@dealerwave/dw-layer-date-converter";
import { formatWithParens, getPinnedCellClassName, isAutoGeneratedFooter } from "@Layouts/components/Tables/DataSmartTable/helpers";
import { getCellClassNameFn } from "@/Common/helpers/Tables";

// Components
import { ExpandCollapseAllButtonHeader } from "@Layouts/components/TreeGrid/ExpandCollapseAllButtonHeader";
import { TypographyCell } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import { TypographyCellProps } from "@/Layouts/components/Tables/DataSmartTable/types";
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";

// Types
import { prepareGroupColDef } from "@/Layouts/components/TreeGrid/prepareGroupColDef";

// Constants
import { HEADER_CLASS_NAME } from "@/Washouts/constants";
import { DATE_FORMAT } from "@/Constants/date";

interface IGenerateRenderingFnConfig {
	customFooterValue: string;
	parentInParens: boolean;
	customFormatFn?: (params: GridRenderCellParams) => string | null;
}

const generateRenderingFn = (
	otherTypographyCellProps?: Partial<Omit<TypographyCellProps, "params">>,
	config: Partial<IGenerateRenderingFnConfig> = {}
) => {
	return (param: GridRenderCellParams) => {
		let formattedValue = config.customFormatFn ? config.customFormatFn(param) : param.formattedValue;

		if (config.parentInParens && param.row?.isParent) {
			formattedValue = formatWithParens(param.value);
		}

		if (config.customFooterValue && isAutoGeneratedFooter(param.id)) {
			formattedValue = config.customFooterValue;
		}

		return <TypographyCell params={{ ...param, formattedValue }} {...otherTypographyCellProps} />;
	};
};

const createCustomRenderCell = () => {
	return generateRenderingFn(
		{ hints: { isMoney: false } },
		{
			customFormatFn: (param) => (typeof param.value === "number" ? `${param.value.toFixed(2)}` : param.value),
		}
	);
};

const getGroupingColDef = (hasNextPage: boolean): GridGroupingColDefOverride => {
	return {
		...prepareGroupColDef(),
		renderHeader: () => (hasNextPage ? null : <ExpandCollapseAllButtonHeader />),
		getApplyQuickFilterFn: () => null,
	};
};

const getBaseColumnDefinition = (): Partial<GridColDef> => ({
	align: "right",
	headerClassName: HEADER_CLASS_NAME,
	renderHeader: (params) => <TypographyHeader params={params} right={params.colDef.align === "right"} />,
	cellClassName: getCellClassNameFn(),
	renderCell: generateRenderingFn({}, { parentInParens: true }),
	resizable: false,
	type: "string",
});

const createDollarRenderCell = (params: GridRenderCellParams) => {
	return <TypographyCell params={params} render={"absolute"} hints={{ isMoney: true, noFocus: true }} />;
};

const createPercentageRenderCell = (customFooterValue: string) => {
	return generateRenderingFn(
		{ hints: { isMoney: false, noFocus: true } },
		{
			customFooterValue,
			customFormatFn: (param) => (typeof param.value === "number" ? `${param.value.toFixed(2)}%` : param.value),
		}
	);
};

const getDepartmentName = (department: string) => {
	const departmentUpper = department?.toUpperCase();
	switch (departmentUpper) {
		case "B":
			return "Body Shop";
		case "S":
			return "Service";
		case "P":
			return "Parts";
		default:
			return department;
	}
};

const BASE_COLUMN_WIDTH = 100;

const getColumnDefinitions = (baseColumnDefinition: Partial<GridColDef>): GridColDef[] => [
	...getBasicColumns(baseColumnDefinition),
	...getLaborColumns(baseColumnDefinition),
	...getPartsColumns(baseColumnDefinition),
	...getOtherColumns(baseColumnDefinition),
];

const getBasicColumns = (baseColumnDefinition: Partial<GridColDef>): GridColDef[] => [
	{
		...baseColumnDefinition,
		field: "_id",
		headerName: "ID",
		width: 10,
		filterable: false,
		getApplyQuickFilterFn: () => null,
	},
	{
		...baseColumnDefinition,
		align: "left",
		field: "userFullName",
		headerName: "Advisor Name",
		width: 150,
		colSpan: 1,
		cellClassName: getPinnedCellClassName,
		renderCell: generateRenderingFn({}, { customFooterValue: "Total Compensation" }),
	},
	{
		...baseColumnDefinition,
		align: "left",
		field: "department",
		valueGetter: (_, row) => getDepartmentName(row?.department),
		headerName: "Department",
		width: 115,
		cellClassName: getPinnedCellClassName,
		renderCell: generateRenderingFn({}, { customFooterValue: " " }),
	},
	{
		...baseColumnDefinition,
		align: "left",
		field: "repairOrderNumber",
		headerName: "Repair Order List",
		cellClassName: getPinnedCellClassName,
		width: 90,
	},
	{
		...baseColumnDefinition,
		field: "date",
		headerName: "RO Date",
		type: "date",
		width: 80,
		align: "left",
		cellClassName: (params) => getPinnedCellClassName(params),
		renderHeader: (params) => <TypographyHeader params={params} left={true} />,
		valueGetter: (value) => value && getDateInUTC(value),
		valueFormatter: (value) => getFormattedDate(value, "", DATE_FORMAT.monthAbbrDatePadded),
	},
	{
		...baseColumnDefinition,
		align: "right",
		field: "repairOrderCount",
		headerName: "RO Count",
		type: "number",
		width: 108,
		cellClassName: (params) => getPinnedCellClassName(params),
		renderHeader: (params) => <TypographyHeader params={params} right className="!mr-3" />,
		renderCell: (params) => <div className="mr-3">{generateRenderingFn({}, {})(params)}</div>,
	},
	{
		field: "__empty",
		headerName: "",
		sortable: false,
		resizable: false,
		filterable: false,
		width: 12,
		headerClassName: "!z-10 !bg-white !border-none !p-0",
		cellClassName: () => "empty-row !bg-white !border-none !p-0",
		renderCell: () => <TypographyCell className={"!bg-blue"} />,
	},
];

const getLaborColumns = (baseColumnDefinition: Partial<GridColDef>): GridColDef[] => [
	{
		...baseColumnDefinition,
		field: "customerPayLaborGrossPercentage",
		headerName: "CP Labor Gross (%)",
		type: "number",
		width: 90,
		valueGetter: (_, row) => row?.customerPay?.labor?.percentage,
		renderCell: createPercentageRenderCell(" "),
	},
	{
		...baseColumnDefinition,
		field: "customerPay.labor.amount",
		headerName: "CP Labor Gross ($)",
		type: "number",
		width: BASE_COLUMN_WIDTH,
		valueGetter: (_, row) => row?.customerPay?.labor?.amount,
		renderHeader: (params) => <TypographyHeader params={params} left={true} />,
		renderCell: (params) => createDollarRenderCell(params),
	},
	{
		...baseColumnDefinition,
		field: "customerPay.labor.hours",
		headerName: "CP Labor Hours",
		type: "number",
		width: 80,
		valueGetter: (_, row) => row?.customerPay?.labor?.hours,
		renderCell: createCustomRenderCell(),
	},
	{
		...baseColumnDefinition,
		field: "warrantyLaborGrossPercentage",
		headerName: "WARR Labor Gross (%)",
		type: "number",
		width: 105,
		valueGetter: (_, row) => row?.warranty?.labor?.percentage,
		renderCell: createPercentageRenderCell(" "),
	},
	{
		...baseColumnDefinition,
		field: "warranty.labor.amount",
		headerName: "WARR Labor Gross ($)",
		type: "number",
		width: 105,
		valueGetter: (_, row) => row?.warranty?.labor?.amount,
		renderHeader: (params) => <TypographyHeader params={params} left={true} />,
		renderCell: (params) => createDollarRenderCell(params),
	},
	{
		...baseColumnDefinition,
		field: "warranty.labor.hours",
		headerName: "WARR Labor Hours",
		type: "number",
		width: 105,
		renderHeader: (params) => (
			<TypographyHeader params={params} right={true} dangerouslySetInnerHTML={{ __html: "WARR Labor<br />Hours" }}>
				<span dangerouslySetInnerHTML={{ __html: "WARR Labor<br />Hours" }} />
			</TypographyHeader>
		),
		valueGetter: (_, row) => row?.warranty?.labor?.hours,
		renderCell: createCustomRenderCell(),
	},
	{
		...baseColumnDefinition,
		field: "internalLaborGrossPercentage",
		headerName: "INTL Labor Gross (%)",
		type: "number",
		width: 90,
		valueGetter: (_, row) => row?.internal?.labor?.percentage,
		renderCell: createPercentageRenderCell(" "),
	},
	{
		...baseColumnDefinition,
		field: "internal.labor.amount",
		headerName: "INTL Labor Gross ($)",
		type: "number",
		width: BASE_COLUMN_WIDTH,
		valueGetter: (_, row) => row?.internal?.labor?.amount,
		renderHeader: (params) => <TypographyHeader params={params} left={true} />,
		renderCell: (params) => createDollarRenderCell(params),
	},
	{
		...baseColumnDefinition,
		field: "internal.labor.hours",
		headerName: "INTL Labor Hours",
		type: "number",
		width: 90,
		valueGetter: (_, row) => row?.internal?.labor?.hours,
		renderCell: createCustomRenderCell(),
	},
];

const getPartsColumns = (baseColumnDefinition: Partial<GridColDef>): GridColDef[] => [
	{
		...baseColumnDefinition,
		field: "customerPayPartsGrossPercentage",
		headerName: "CP Parts Gross (%)",
		type: "number",
		width: 80,
		valueGetter: (_, row) => row?.customerPay?.parts?.percentage,
		renderCell: createPercentageRenderCell(" "),
	},
	{
		...baseColumnDefinition,
		field: "customerPay.parts.amount",
		headerName: "CP Parts Gross ($)",
		type: "number",
		width: BASE_COLUMN_WIDTH,
		valueGetter: (_, row) => row?.customerPay?.parts?.amount,
		renderHeader: (params) => <TypographyHeader params={params} left={true} />,
		renderCell: (params) => createDollarRenderCell(params),
	},
	{
		...baseColumnDefinition,
		field: "warrantyPartsGrossPercentage",
		headerName: "WARR Parts Gross (%)",
		type: "number",
		width: BASE_COLUMN_WIDTH,
		valueGetter: (_, row) => row?.warranty?.parts?.percentage,
		renderCell: createPercentageRenderCell(" "),
	},
	{
		...baseColumnDefinition,
		field: "warranty.parts.amount",
		headerName: "WARR Parts Gross ($)",
		type: "number",
		width: 110,
		valueGetter: (_, row) => row?.warranty?.parts?.amount,
		renderHeader: (params) => <TypographyHeader params={params} left={true} />,
		renderCell: (params) => createDollarRenderCell(params),
	},
	{
		...baseColumnDefinition,
		field: "internalPartsGrossPercentage",
		headerName: "INTL Parts Gross (%)",
		type: "number",
		width: 90,
		valueGetter: (_, row) => row?.internal?.parts?.percentage,
		renderCell: createPercentageRenderCell(" "),
	},
	{
		...baseColumnDefinition,
		field: "internal.parts.amount",
		headerName: "INTL Parts Gross ($)",
		type: "number",
		width: 110,
		valueGetter: (_, row) => row?.internal?.parts?.amount,
		renderHeader: (params) => <TypographyHeader params={params} left={true} />,
		renderCell: (params) => createDollarRenderCell(params),
	},
];

const getOtherColumns = (baseColumnDefinition: Partial<GridColDef>): GridColDef[] => [
	{
		...baseColumnDefinition,
		field: "subletGross",
		headerName: "Sublet GRS ($)",
		type: "number",
		width: 130,
		renderHeader: (params) => <TypographyHeader params={params} left={true} />,
		renderCell: (params) => <div className="mr-6">{createDollarRenderCell(params)}</div>,
	},
	{
		...baseColumnDefinition,
		field: "discountPercentage",
		headerName: "Disc. (%)",
		type: "number",
		width: BASE_COLUMN_WIDTH,
		renderCell: createPercentageRenderCell(" "),
	},
	{
		...baseColumnDefinition,
		field: "discountAmount",
		headerName: "Disc. ($)",
		type: "number",
		width: BASE_COLUMN_WIDTH,
		renderHeader: (params) => <TypographyHeader params={params} left={true} />,
		renderCell: (params) => createDollarRenderCell(params),
	},
	{
		...baseColumnDefinition,
		field: "totalGross",
		headerName: "Total ($)",
		type: "number",
		width: 110,
		renderHeader: (params) => <TypographyHeader params={params} left={true} />,
		renderCell: (params) => createDollarRenderCell(params),
	},
];

export default function useAdvisorPerformanceTableConfig(
	apiRef: RefObject<GridApi>,
	hasNextPage = false
): { columns: GridColDef[]; groupingColDef: GridGroupingColDefOverride } {
	const groupingColDef = useMemo(() => getGroupingColDef(hasNextPage), [hasNextPage]);

	const baseColumnDefinition = getBaseColumnDefinition();

	const columns = useMemo(() => getColumnDefinitions(baseColumnDefinition), [apiRef]);

	return { columns, groupingColDef };
}
